import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import styles from "./AudioVisualizer.module.css";

interface AudioVisualizerProps {
  isActive: boolean;
  className?: string;
  audioStream?: MediaStream | null; // Optional shared audio stream
}

// Extend Window interface for webkit support
declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

/**
 * AudioVisualizer Component
 *
 * Creates a real-time sound wave visualization based on actual microphone input.
 * Uses Web Audio API to analyze audio frequency data and render animated bars.
 * Optimized with memoization and consolidated drawing logic.
 */
const AudioVisualizerComponent: React.FC<AudioVisualizerProps> = ({
  isActive,
  className = "",
  audioStream = null,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get and cache canvas context
  const getCanvasContext = useCallback(() => {
    if (!ctxRef.current && canvasRef.current) {
      ctxRef.current = canvasRef.current.getContext("2d");
    }
    return ctxRef.current;
  }, []);

  // Memoize animation constants to avoid recalculation
  const animationConfig = useMemo(
    () => ({
      numBars: 16,
      fftSize: 1024,
      smoothingTimeConstant: 0.3,
      minDecibels: -100,
      maxDecibels: -20,
      frameRate: 0.08,
      fallbackHeight: 0.4,
      fallbackRandomness: 0.1,
    }),
    []
  );

  // Cleanup audio resources
  const cleanup = useCallback(() => {
    // Stop animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }

    // Only stop media stream if we own it (not shared)
    if (streamRef.current && !audioStream) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Reset refs
    audioContextRef.current = null;
    analyzerRef.current = null;
    dataArrayRef.current = null;
    sourceRef.current = null;
    ctxRef.current = null;

    setHasPermission(false);
    setError(null);
  }, [audioStream]);

  // Consolidated drawing function for both real and fallback animations
  const drawVisualization = useCallback(
    (isFallback = false) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const ctx = getCanvasContext();
      if (!ctx) return null;

      const { width, height } = canvas;
      const { numBars } = animationConfig;
      const barWidth = width / numBars;

      let frame = 0;

      const draw = () => {
        if (!isActive) return;

        ctx.clearRect(0, 0, width, height);

        if (isFallback) {
          // Fallback animation
          for (let i = 0; i < numBars; i++) {
            const waveValue =
              Math.sin(frame * animationConfig.frameRate + i * 0.5) *
                animationConfig.fallbackHeight +
              0.5;
            const randomness = Math.random() * animationConfig.fallbackRandomness;
            const barHeight =
              (waveValue + randomness) * height * animationConfig.fallbackHeight + 8;

            const x = i * barWidth + barWidth * 0.1;
            const y = (height - barHeight) / 2;

            const intensity = waveValue + randomness;
            ctx.fillStyle = `hsla(30, 70%, ${50 + intensity * 25}%, 0.7)`;
            ctx.fillRect(x, y, barWidth * 0.8, barHeight);
          }
          frame++;
        } else {
          // Real audio visualization
          const analyzer = analyzerRef.current;
          const dataArray = dataArrayRef.current;

          if (!analyzer || !dataArray || !hasPermission) return;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          analyzer.getByteFrequencyData(dataArray as any);
          const dataStep = Math.floor(dataArray.length / numBars);

          for (let i = 0; i < numBars; i++) {
            const dataIndex = i * dataStep;
            let value = 0;
            const binCount = Math.max(1, Math.floor(dataStep / 2));

            for (let j = 0; j < binCount; j++) {
              value += dataArray[dataIndex + j] || 0;
            }
            value = value / binCount / 255;

            // Frequency-based scaling
            const frequencyMultiplier = i < 3 ? 1.2 : i < 8 ? 1.0 : 1.5;
            const smoothedValue = Math.max(0.02, value * frequencyMultiplier);
            const barHeight = smoothedValue * height * 0.85;

            const x = i * barWidth + barWidth * 0.1;
            const y = (height - barHeight) / 2;

            // Color gradient
            const freqPosition = i / (numBars - 1);
            const hue = 15 + freqPosition * 45 + smoothedValue * 20;
            const saturation = 70 + smoothedValue * 25;
            const lightness = 45 + smoothedValue * 30;
            const alpha = 0.8 + smoothedValue * 0.2;

            ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
            ctx.fillRect(x, y, barWidth * 0.8, barHeight);

            // Glow effect for high intensity
            if (smoothedValue > 0.6) {
              ctx.shadowColor = `hsla(${hue}, ${saturation}%, ${lightness + 20}%, 0.6)`;
              ctx.shadowBlur = 6;
              ctx.fillRect(x, y, barWidth * 0.8, barHeight);
              ctx.shadowBlur = 0;
            }
          }
        }

        animationFrameRef.current = requestAnimationFrame(draw);
      };

      return draw;
    },
    [isActive, hasPermission, animationConfig, getCanvasContext]
  );

  // Initialize audio context and microphone access
  useEffect(() => {
    if (!isActive) {
      cleanup();
      return;
    }

    const initializeAudio = async () => {
      try {
        let stream: MediaStream;

        if (audioStream) {
          // Use provided shared audio stream
          console.log("Using shared audio stream for visualization");
          stream = audioStream;
        } else {
          // Request our own microphone access
          console.log("Requesting microphone access for audio visualization...");
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: false, // Turn off for better frequency analysis
              noiseSuppression: false, // Turn off for more natural visualization
              autoGainControl: false, // Prevent auto-leveling for consistent visualization
              sampleRate: 44100,
            },
          });
        }

        // Only store stream reference if we created it (not shared)
        if (!audioStream) {
          streamRef.current = stream;
        }

        // Create audio context with fallback for webkit
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
          throw new Error("Web Audio API not supported");
        }

        const audioContext = new AudioContextClass();

        // Resume audio context if it's suspended (required by some browsers)
        if (audioContext.state === "suspended") {
          console.log("Resuming suspended audio context...");
          await audioContext.resume();
        }

        audioContextRef.current = audioContext;

        // Create analyzer node with settings optimized for visualization
        const analyzer = audioContext.createAnalyser();
        analyzer.fftSize = animationConfig.fftSize;
        analyzer.smoothingTimeConstant = animationConfig.smoothingTimeConstant;
        analyzer.minDecibels = animationConfig.minDecibels;
        analyzer.maxDecibels = animationConfig.maxDecibels;
        analyzerRef.current = analyzer;

        // Create audio source from stream
        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        source.connect(analyzer);

        // Create data array for frequency data
        const bufferLength = analyzer.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        dataArrayRef.current = dataArray;

        console.log("Audio visualization setup complete. Buffer length:", bufferLength);
        setHasPermission(true);
        setError(null);

        // Start real-time visualization
        const draw = drawVisualization(false);
        if (draw) draw();
      } catch (err) {
        console.warn("Microphone access denied or failed, using fallback animation:", err);
        setError(""); // Don't show error message, fallback looks good
        setHasPermission(false);

        // Start fallback animation instead
        const draw = drawVisualization(true);
        if (draw) draw();
      }
    };

    initializeAudio();

    return cleanup;
  }, [isActive, audioStream, cleanup, drawVisualization, animationConfig]);

  return (
    <div className={`${styles.audioVisualizer} ${className}`}>
      <canvas
        ref={canvasRef}
        width={200}
        height={40}
        className={styles.visualizerCanvas}
        aria-label="Real-time audio level visualization"
      />
      {error && !hasPermission && (
        <div className={styles.errorMessage} aria-live="polite" role="status">
          {error}
        </div>
      )}
    </div>
  );
};

// Export memoized component to prevent unnecessary re-renders
export const AudioVisualizer = React.memo(AudioVisualizerComponent);
